import {
  ApiResponse,
  SessionResponse,
  ExperimentResponse,
  ScrollCollectStatusResponse,
  ScrollCollectSettingsResponse,
  EligibilityResponse,
  ProductFeedItem,
  ProductDetails,
  CompleteScrollCollectRequest,
  EventTrackingRequest,
  RequestParams,
} from '../types/api';
import { CUSTOMER_ENDPOINTS, ADMIN_ENDPOINTS } from './base';
import { ApiManager } from '../ApiManager';

/**
 * Customer API endpoint methods for Shopify app proxy
 */
export class CustomerEndpoints {
  private baseUrl: string;

  constructor(baseUrl: string = 'https://example.com') {
    this.baseUrl = baseUrl;
  }

  /**
   * Helper method to format array parameters for query strings
   * Converts ['value1', 'value2'] to { 'param[]': 'value1', 'param[]': 'value2' }
   */
  private formatArrayParams(params: Record<string, any>): Record<string, string> {
    const formattedParams: Record<string, string> = {};

    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // For arrays, we need to handle them specially since URLSearchParams.append doesn't work as expected
        // We'll add them with array notation and handle multiple values in makeRequest
        formattedParams[`${key}[]`] = value.join(','); // We'll split this back in makeRequest
      } else if (value !== undefined && value !== null) {
        formattedParams[key] = String(value);
      }
    });

    return formattedParams;
  }

  /**
   * Get or create a session ID for tracking
   */
  async getSession(): Promise<ApiResponse<SessionResponse>> {
    return this.makeRequest<SessionResponse>(CUSTOMER_ENDPOINTS.SESSION);
  }

  /**
   * Get experiment group assignment for A/B testing
   */
  async getExperiment(topic: string, sessionId?: string): Promise<ApiResponse<ExperimentResponse>> {
    const params: Record<string, any> = { topic };

    // Add session_id if provided
    if (sessionId) {
      params.session_id = sessionId;
    }

    return this.makeRequest<ExperimentResponse>(CUSTOMER_ENDPOINTS.EXPERIMENT, params);
  }

  /**
   * Get current scroll-and-collect activity status
   */
  async getScrollCollectStatus(): Promise<ApiResponse<ScrollCollectStatusResponse>> {
    return this.makeRequest<ScrollCollectStatusResponse>(CUSTOMER_ENDPOINTS.SCROLL_COLLECT_STATUS);
  }

  /**
   * Complete scroll-and-collect activity and apply discount
   */
  async completeScrollCollect(
    requestBody: CompleteScrollCollectRequest,
    sessionId?: string
  ): Promise<ApiResponse<ScrollCollectStatusResponse>> {
    const params: Record<string, any> = {};

    // Add session_id if provided
    if (sessionId) {
      params.session_id = sessionId;
    }

    return this.makeRequest<ScrollCollectStatusResponse>(
      CUSTOMER_ENDPOINTS.SCROLL_COLLECT_COMPLETE,
      params,
      requestBody
    );
  }

  /**
   * Get activity settings and translations
   */
  async getScrollCollectSettings(
    language: string,
    experiments: string,
    sessionId?: string
  ): Promise<ApiResponse<ScrollCollectSettingsResponse>> {
    const params: Record<string, any> = {
      language: language.toUpperCase(),
    };

    // Add experiments as comma-separated key:value pairs if provided
    if (experiments) {
      params.experiments = experiments;
    }

    // Add session_id if provided
    if (sessionId) {
      params.session_id = sessionId;
    }

    return this.makeRequest<ScrollCollectSettingsResponse>(CUSTOMER_ENDPOINTS.SCROLL_COLLECT_SETTINGS, params);
  }

  /**
   * Check if customer is eligible for activity
   */
  async getScrollCollectEligibility(
    experiments: string,
    sessionId?: string
  ): Promise<ApiResponse<EligibilityResponse>> {
    const params: Record<string, any> = {};

    // Add experiments as comma-separated key:value pairs if provided
    if (experiments) {
      params.experiments = experiments;
    }

    if (sessionId) {
      params.session_id = sessionId;
    }
    return this.makeRequest<EligibilityResponse>(CUSTOMER_ENDPOINTS.SCROLL_COLLECT_ELIGIBLE, params);
  }

  /**
   * Get personalized product feed for a department
   */
  async getScrollCollectFeed(
    country: string,
    language: string,
    departmentId: number,
    experiments: string,
    sessionId?: string
  ): Promise<ApiResponse<ProductFeedItem[]>> {
    const params: Record<string, any> = {
      country: country.toUpperCase(),
      language: language.toUpperCase(),
      department_id: departmentId.toString(),
    };

    // Add experiments as comma-separated key:value pairs if provided
    if (experiments) {
      params.experiments = experiments;
    }

    if (sessionId) {
      params.session_id = sessionId;
    }

    return this.makeRequest<ProductFeedItem[]>(CUSTOMER_ENDPOINTS.SCROLL_COLLECT_FEED, params);
  }

  /**
   * Get detailed product information
   */
  async getScrollCollectProduct(
    productId: number,
    country: string,
    language: string,
    experiments: string
  ): Promise<ApiResponse<ProductDetails>> {
    const endpoint = {
      ...CUSTOMER_ENDPOINTS.SCROLL_COLLECT_PRODUCT,
      url: `${CUSTOMER_ENDPOINTS.SCROLL_COLLECT_PRODUCT.url}/${productId}`,
    };
    const params: Record<string, any> = {
      country: country.toUpperCase(),
      language: language.toUpperCase(),
    };

    // Add experiments as comma-separated key:value pairs if provided
    if (experiments) {
      params.experiments = experiments;
    }

    return this.makeRequest<ProductDetails>(endpoint, params);
  }

  /**
   * Track activity events
   */
  async trackScrollCollectEvent(eventData: EventTrackingRequest): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(CUSTOMER_ENDPOINTS.SCROLL_COLLECT_EVENT, {}, eventData);
  }

  /**
   * Manual authorization code grant endpoint for Shopify admin authentication
   */
  async getAdminAuth(): Promise<ApiResponse<void>> {
    const fullUrl = `${this.baseUrl}${ADMIN_ENDPOINTS.AUTH.url}`;

    try {
      return await ApiManager.request<void>({ url: fullUrl, method: ADMIN_ENDPOINTS.AUTH.method });
    } catch (error) {
      throw new Error(`Admin auth failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Generic method to make API requests with retry logic
   * Authentication is handled automatically by Shopify app proxy
   */
  private async makeRequest<T>(
    endpoint: { url: string; method?: string },
    params: Record<string, any> = {},
    body?: any
  ): Promise<ApiResponse<T>> {
    const fullUrl = new URL(endpoint.url, this.baseUrl).toString();

    // Format array parameters properly
    const formattedParams = this.formatArrayParams(params);

    // For GET requests with array parameters, we need to build the URL manually
    // to ensure proper array notation like experiments[]=value1&experiments[]=value2
    let urlWithParams = fullUrl;
    if (Object.keys(formattedParams).length > 0) {
      const url = new URL(fullUrl);

      Object.entries(formattedParams).forEach(([key, value]) => {
        if (key.endsWith('[]') && value.includes(',')) {
          // Handle array parameters - split comma-separated values and add each as separate parameter
          const arrayValues = value.split(',');
          arrayValues.forEach((arrayValue) => {
            url.searchParams.append(key, arrayValue.trim());
          });
        } else {
          url.searchParams.append(key, value);
        }
      });

      urlWithParams = url.toString();
    }

    try {
      const method = (endpoint.method?.toUpperCase() as 'GET' | 'POST' | 'PUT' | 'DELETE') || 'GET';

      switch (method) {
        case 'GET':
        case 'DELETE':
          // For GET/DELETE, use the manually built URL with proper array parameters
          return await ApiManager.request<T>({ url: urlWithParams, method }, {});
        case 'POST':
          // For POST, use URL with parameters if any parameters are provided
          return await ApiManager.post<T>({ url: urlWithParams }, body);
        case 'PUT':
          // For PUT, use URL with parameters if any parameters are provided
          return await ApiManager.put<T>({ url: urlWithParams }, body);
        default:
          return await ApiManager.get<T>({ url: urlWithParams }, {});
      }
    } catch (error) {
      throw new Error(`API request failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Update the base URL for the customer endpoints
   */
  updateBaseUrl(baseUrl: string): void {
    this.baseUrl = baseUrl;
  }

  /**
   * Get the current base URL
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }
}
